import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog.tsx';
import { Button } from '@/components/ui/button.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Label } from '@/components/ui/label.tsx';
import { Textarea } from '@/components/ui/textarea.tsx';
import { Funnel } from '@/types/funnel.ts';
import { ChevronDown, ChevronUp, Link2 } from 'lucide-react';

interface EditFunnelModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    funnel: Funnel | null;
    onSubmit: (data: { nome?: string; descricao?: string; utm_source?: string; utm_medium?: string; utm_campaign?: string }) => void;
    isLoading: boolean;
}

export default function EditFunnelModal({
                                            open,
                                            onOpenChange,
                                            funnel,
                                            onSubmit,
                                            isLoading
                                        }: EditFunnelModalProps) {
    const [formData, setFormData] = useState({
        nome: '',
        descricao: '',
        utm_source: '',
        utm_medium: '',
        utm_campaign: '',
    });
    const [showUtm, setShowUtm] = useState(false);

    useEffect(() => {
        if (funnel) {
            setFormData({
                nome: funnel.nome,
                descricao: funnel.descricao || '',
                utm_source: funnel.utm_source || '',
                utm_medium: funnel.utm_medium || '',
                utm_campaign: funnel.utm_campaign || '',
            });
            setShowUtm(!!(funnel.utm_source || funnel.utm_medium || funnel.utm_campaign));
        }
    }, [funnel]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit(formData);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Editar Funil</DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                    {/* Nome */}
                    <div>
                        <Label htmlFor="edit-nome">Nome do Funil</Label>
                        <Input
                            id="edit-nome"
                            value={formData.nome}
                            onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                            placeholder="Nome do funil"
                        />
                    </div>

                    {/* Descricao */}
                    <div>
                        <Label htmlFor="edit-descricao">Descricao</Label>
                        <Textarea
                            id="edit-descricao"
                            value={formData.descricao}
                            onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                            placeholder="Descricao do funil..."
                            rows={3}
                        />
                    </div>

                    {/* UTM Parameters */}
                    <div className="space-y-3">
                        <button
                            type="button"
                            onClick={() => setShowUtm(!showUtm)}
                            className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                        >
                            <Link2 className="h-4 w-4" />
                            Parametros UTM
                            {showUtm ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </button>

                        {showUtm && (
                            <div className="space-y-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <p className="text-xs text-gray-500">
                                    Parametros UTM serao adicionados automaticamente as URLs dos botoes no fluxo.
                                </p>
                                <div className="space-y-2">
                                    <Label htmlFor="edit-utm_source" className="text-sm">utm_source</Label>
                                    <Input
                                        id="edit-utm_source"
                                        value={formData.utm_source}
                                        onChange={(e) => setFormData({ ...formData, utm_source: e.target.value })}
                                        placeholder="Ex: facebook, instagram"
                                        disabled={isLoading}
                                        className="w-full"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="edit-utm_medium" className="text-sm">utm_medium</Label>
                                    <Input
                                        id="edit-utm_medium"
                                        value={formData.utm_medium}
                                        onChange={(e) => setFormData({ ...formData, utm_medium: e.target.value })}
                                        placeholder="Ex: chatbot, messenger"
                                        disabled={isLoading}
                                        className="w-full"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="edit-utm_campaign" className="text-sm">utm_campaign</Label>
                                    <Input
                                        id="edit-utm_campaign"
                                        value={formData.utm_campaign}
                                        onChange={(e) => setFormData({ ...formData, utm_campaign: e.target.value })}
                                        placeholder="Ex: vendas_janeiro, black_friday"
                                        disabled={isLoading}
                                        className="w-full"
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-3 pt-4">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={isLoading}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            disabled={isLoading}
                            className="bg-[#0f172a] hover:bg-[#0a1120] text-white"
                        >
                            {isLoading ? 'Salvando...' : 'Salvar Alteracoes'}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}